import { groupPageQuery } from "@/queries/group"
import {
  KirbyStudentPreviewData,
  studentPagePreviewQuery,
} from "@/queries/student"
import { PathUtils } from "@/utils/path.utils"
import { computed, ref } from "vue"

const API = PathUtils.strip(import.meta.env.VITE_KIRBY_URL) + "/api/query"

export const useSiblings = async (page: KirbyStudentPreviewData) => {
  const siblings = ref<KirbyStudentPreviewData[]>([])
  const siblingsSorted = computed(() => {
    return siblings.value.sort((a, b) => {
      return a.name.localeCompare(b.name)
    })
  })

  const getNextSibling = (id: string): KirbyStudentPreviewData | undefined => {
    // Get next index from sorted siblings
    // If the current index is the last one, return the first sibling
    const index = siblingsSorted.value.findIndex(sibling => sibling.id === id)
    if (index === -1) return undefined
    const nextSibling =
      siblingsSorted.value[(index + 1) % siblingsSorted.value.length]
    return nextSibling.id === id ? undefined : nextSibling
  }

  const getPrevSibling = (id: string): KirbyStudentPreviewData | undefined => {
    // Get previous index from sorted siblings
    // If the current index is the first one, return the last sibling
    const index = siblingsSorted.value.findIndex(sibling => sibling.id === id)
    if (index === -1) return undefined
    const prevSibling =
      siblingsSorted.value[
        (index - 1 + siblingsSorted.value.length) % siblingsSorted.value.length
      ]
    return prevSibling.id === id ? undefined : prevSibling
  }

  const paginationData = await fetch(API, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      query: "site",
      select: {
        groupProjects: {
          query: `site.find("${page.programme.id}").children.filterBy('thumbnail', '!=', '')`,
          select: groupPageQuery,
        },
        siblings: {
          query: `site.find('students').children.filterBy('programme', '$=', '${page.programme.uuid}').filterBy('thumbnail', '!=', '')`,
          select: studentPagePreviewQuery,
        },
      },
    }),
  }).catch(e => {
    throw new Error("Failed to fetch Kirby JSON")
  })

  const json = await paginationData.json().catch(e => {
    throw new Error("Failed to parse Kirby JSON")
  })

  const data = json.result
  siblings.value = [...data.siblings, ...data.groupProjects]

  const getSiblings = (
    id: string,
    count: number,
  ): KirbyStudentPreviewData[] => {
    const sorted = siblingsSorted.value
    const index = sorted.findIndex(s => s.id === id)
    if (index === -1 || sorted.length <= 1) return []
    const results: KirbyStudentPreviewData[] = []
    for (let i = 1; results.length < count && i < sorted.length; i++) {
      results.push(sorted[(index + i) % sorted.length])
    }
    return results
  }

  return {
    siblings,
    siblingsSorted,
    getNextSibling,
    getPrevSibling,
    getSiblings,
  }
}
